import React, { useEffect, useState, useRef } from 'react';
import { Modal, Spin, Alert } from 'antd';
import { EnvironmentOutlined, LoadingOutlined } from '@ant-design/icons';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import io from 'socket.io-client';

const mapContainerStyle = {
  width: '100%',
  height: '500px',
};

// const defaultCenter = {
//   lat: 37.7749,
//   lng: -122.4194,
// };

export const UberDriverMapModal = ({ visible, onClose, rideId, apiUrl }) => {
  const [driverLocation, setDriverLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  });

  useEffect(() => {
    if (!visible || !rideId) return;

    // Connect to WebSocket
    const socketUrl = apiUrl || window.location.origin.replace('http', 'ws');
    const socket = io(socketUrl, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('WebSocket connected');
      socket.emit('subscribe_ride', { rideId });
      setLoading(false);
    });

    socket.on('location_update', (data) => {
      if (data.rideId === rideId) {
        setDriverLocation({
          lat: data.latitude,
          lng: data.longitude,
          driver: data.driver,
          uberStatus: data.uberStatus,
        });
        setError(null);
      }
    });

    socket.on('connect_error', (err) => {
      console.error('WebSocket connection error:', err);
      setError('Failed to connect to real-time tracking service');
      setLoading(false);
    });

    socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('unsubscribe_ride', { rideId });
        socketRef.current.disconnect();
      }
    };
  }, [visible, rideId, apiUrl]);

  const handleClose = () => {
    if (socketRef.current) {
      socketRef.current.emit('unsubscribe_ride', { rideId });
      socketRef.current.disconnect();
    }
    onClose();
  };

  return (
    <Modal
      title={
        <div>
          <EnvironmentOutlined style={{ marginRight: '8px' }} />
          Real-Time Driver Location
        </div>
      }
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={800}
      centered
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          <p style={{ marginTop: '16px' }}>Connecting to tracking service...</p>
        </div>
      )}

      {error && (
        <Alert
          message="Connection Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {!loading && !error && !driverLocation && (
        <Alert
          message="Waiting for Driver Location"
          description="The driver's location will appear here once they begin the trip."
          type="info"
          showIcon
        />
      )}

      {!loading && !error && driverLocation && isLoaded && (
        <div>
          {driverLocation.driver && (
            <div style={{
              marginBottom: '12px',
              padding: '12px',
              backgroundColor: '#f0f2f5',
              borderRadius: '4px'
            }}>
              <p style={{ margin: '4px 0' }}>
                <strong>Driver:</strong> {driverLocation.driver.name}
              </p>
              <p style={{ margin: '4px 0' }}>
                <strong>Status:</strong> {driverLocation.uberStatus}
              </p>
            </div>
          )}

          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={driverLocation}
            zoom={15}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
            }}
          >
            <Marker
              position={driverLocation}
              icon={{
                url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                scaledSize: new window.google.maps.Size(40, 40),
              }}
              title="Driver Location"
            />
          </GoogleMap>
        </div>
      )}
    </Modal>
  );
};
